import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Target,
  CalendarDays,
  Briefcase,
  TrendingUp,
  BarChart3,
  Settings,
  ChevronLeft,
  ChevronRight,
  Building2,
  FileText,
  Key,
  Webhook,
  Mail,
  FolderArchive,
  Tag,
  MessageSquare,
  Bot,
  Send,
  Database,
  X
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useAuth } from '../../contexts/AuthContext';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { Button } from '../ui/button';

const PRODUCT_NAME = process.env.REACT_APP_PRODUCT_NAME || 'SalesTrack CRM';
const PRODUCT_TAGLINE = process.env.REACT_APP_PRODUCT_TAGLINE || 'Incentive Tracker';

const menuItems = [
  { path: '/dashboard', icon: LayoutDashboard, label: 'Overview' },
  { path: '/leads', icon: Target, label: 'Leads' },
  { path: '/demos', icon: CalendarDays, label: 'Demos' },
  { path: '/deals', icon: Briefcase, label: 'Deals' },
  { path: '/clients', icon: Building2, label: 'Clients' },
  { path: '/team', icon: Users, label: 'Team' },
  { path: '/incentives', icon: TrendingUp, label: 'Incentives', requiresIncentiveAccess: true },
  { path: '/reports', icon: BarChart3, label: 'Reports' },
];

const adminItems = [
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/plans', icon: FileText, label: 'Plans' },
  { path: '/admin/invoices', icon: FileText, label: 'Invoices' },
  { path: '/admin/incentive-config', icon: Settings, label: 'Incentive Config' },
  { path: '/admin/api-config', icon: Key, label: 'API Config' },
  { path: '/admin/webhooks', icon: Webhook, label: 'Webhooks' },
  { path: '/admin/whatsapp-ai', icon: Bot, label: 'WhatsApp AI' },
  // { path: '/admin/wa-campaigns', icon: Send, label: 'WA Campaigns' }, // Hidden - Feature in development
  { path: '/admin/master-lead-data', icon: Database, label: 'Master Lead Data' },
  { path: '/admin/sso', icon: Key, label: 'SSO' },
  { path: '/admin/email-config', icon: Mail, label: 'Email/SMTP' },
  { path: '/admin/lead-bank', icon: FolderArchive, label: 'Lead Bank' },
  { path: '/admin/tags', icon: Tag, label: 'Tags' },
  { path: '/admin/whatsapp-templates', icon: MessageSquare, label: 'WA Templates' },
];

const Sidebar = ({ collapsed, setCollapsed, onClose, isMobile }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { canSeeIncentives } = usePrivacy();
  const isAdmin = user?.role === 'ADMIN';
  const isManagerOrAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';

  // Filter menu items based on role and incentive visibility
  const filteredMenuItems = menuItems.filter(item => {
    // Hide Team page for employees
    if (item.path === '/team' && !isManagerOrAdmin) return false;
    // Hide Incentives if access is restricted
    if (item.requiresIncentiveAccess && !canSeeIncentives()) return false;
    return true;
  });

  return (
    <div
      className={cn(
        'h-screen bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700 flex flex-col transition-all duration-300',
        isMobile ? 'w-[280px]' : (collapsed ? 'w-[70px]' : 'w-[240px]')
      )}
    >
      {/* Logo Section */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-700">
        <Link to="/dashboard" className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center text-white font-bold text-lg shadow-lg">
            {PRODUCT_NAME.charAt(0)}
          </div>
          {(!collapsed || isMobile) && (
            <div className="flex flex-col">
              <span className="font-bold text-slate-800 dark:text-white text-sm leading-tight">
                {PRODUCT_NAME}
              </span>
              <span className="text-[10px] text-slate-500 dark:text-slate-400">
                {PRODUCT_TAGLINE}
              </span>
            </div>
          )}
        </Link>
        
        {isMobile && (
          <Button variant="ghost" size="icon" onClick={onClose} className="lg:hidden">
            <X className="w-5 h-5" />
          </Button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {filteredMenuItems.map((item) => {
            const isActive = location.pathname === item.path || 
              (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => isMobile && onClose?.()}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm',
                    isActive
                      ? 'bg-slate-800 text-white dark:bg-slate-700'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  )}
                >
                  <item.icon className={cn('w-5 h-5 flex-shrink-0', collapsed && !isMobile && 'mx-auto')} />
                  {(!collapsed || isMobile) && <span className="truncate">{item.label}</span>}
                </Link>
              </li>
            );
          })}
        </ul>

        {/* Admin Section - ADMIN ONLY */}
        {isAdmin && (
          <>
            <div className={cn(
              'mt-6 mb-2 px-3 text-xs font-semibold text-slate-400 uppercase tracking-wider',
              collapsed && !isMobile && 'text-center'
            )}>
              {(!collapsed || isMobile) ? 'Admin' : '•'}
            </div>
            <ul className="space-y-1">
              {adminItems.map((item) => {
                const isActive = location.pathname === item.path;
                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => isMobile && onClose?.()}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all text-sm',
                        isActive
                          ? 'bg-slate-800 text-white dark:bg-slate-700'
                          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                      )}
                    >
                      <item.icon className={cn('w-5 h-5 flex-shrink-0', collapsed && !isMobile && 'mx-auto')} />
                      {(!collapsed || isMobile) && <span className="truncate">{item.label}</span>}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </nav>

      {/* Collapse Toggle - Desktop only */}
      {!isMobile && (
        <div className="p-2 border-t border-slate-200 dark:border-slate-700">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="w-full flex items-center justify-center py-2 text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            {collapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
